"use client"

import * as React from "react"
import { useTranslations } from "next-intl"

import {
  decrementOption,
  firstUnsatisfiedGroupIndex,
  initialGroupPicks,
  isGroupAtMax,
  isGroupSatisfied,
  isSelectionRequired,
  normalizeGroupPicks,
  optionTiles,
  resolveSelectionIndicator,
  tapOption,
  type MenuItem,
  type ModifierGroup,
  type ModifierOption,
  type OptionPick,
  type OptionTileState,
  type SelectionRule,
} from "@workspace/model"
import { Button } from "@workspace/ui/components/button"
import {
  OptionTile,
  OptionTileList,
} from "@workspace/ui/components/option-tile"
import { cn } from "@workspace/ui/lib/utils"

import { formatMoney, formatMoneyDelta } from "@/lib/money"
import { displayGroupIndices } from "@/lib/group-order"
import { optionPriceDelta } from "@/lib/free-option-preview"
import {
  extrasAllowList,
  isLegacyFreeOptionGroup,
  legacyFreeOptionDefaultIds,
  legacyNextUnitFree,
  optionAmount,
  previewOptionSelection,
  type OptionPricingConfig,
  type PreviewRow,
} from "@/lib/option-pricing-preview"
import {
  absoluteSizePrice,
  isSizeGroup,
  sortSizeOptions,
} from "@/lib/size-group"

import { addModifiersToCartAction } from "../../cart/actions"
import { type ItemUnavailableReason } from "@/lib/availability"
import { SpecialInstructionsField } from "@/components/special-instructions-field"
import { AddToCartForm, AddToCartSubmitButton } from "./add-to-cart-form"

const MIN_QUANTITY = 1
const MAX_QUANTITY = 10

/** The item-level configs layered onto its groups (free toppings / extras allow-list). */
export function pricingConfigOf(
  item: MenuItem
): OptionPricingConfig | undefined {
  const { freeOption, extrasLimit } = item.composite ?? {}
  return freeOption || extrasLimit ? { freeOption, extrasLimit } : undefined
}

/**
 * Starting picks per group: the engine's defaults + group-level auto-added free
 * options, or — for a group the LEGACY item-level free-option config governs —
 * that config's default set (`autoAdd` ⇒ the first eligible ids). Never an
 * option the extras allow-list forbids.
 */
export function initialSelection(
  groups: readonly ModifierGroup[],
  config: OptionPricingConfig | undefined
): OptionPick[][] {
  return groups.map((group) => {
    const picks =
      config?.freeOption && isLegacyFreeOptionGroup(group, config)
        ? normalizeGroupPicks(
            group,
            legacyFreeOptionDefaultIds(group, config.freeOption).map(
              (optionId) => ({ optionId })
            )
          )
        : initialGroupPicks(group)
    const allowed = extrasAllowList(group, config)
    return allowed ? picks.filter((pick) => allowed.has(pick.optionId)) : picks
  })
}

/**
 * The item customizer (US-018, reworked for contract v0.28.0): every modifier
 * group of a plain OR free-option/extras-limit item, driven by the shared
 * selection engine in `@workspace/model` (`item-option-selection.ts`, a port of
 * itd_cater_app's `ItemOptionSelectionEngine`) so a tap here obeys exactly the
 * rules the server re-prices with:
 *
 *  - `selection.indicator` picks the look (radio / checkbox / tile highlight);
 *  - `optionType`: select-all ("Everything") ticks every simple option as
 *    included, "None" clears the group, multi-quantity options get a stepper;
 *  - `selection.max` blocks further counted picks; a required group (or a
 *    positive `min`) blocks Add until answered, scrolling to the first gap;
 *  - `group.freeOption` makes the first eligible units free ("Free" badges,
 *    "n of m free" counter); the legacy item-level `composite.freeOption`
 *    still governs multi groups without a group config of their own, and
 *    `composite.extrasLimit` disables extras outside the allow-list.
 *
 * Selections are keyed by a group's POSITION in `item.modifierGroups`, not its
 * id: a group id is not unique within an item (the live caterapi menu tags
 * extras groups `extras:<categoryId>`). Groups RENDER options-first, extras
 * last (`displayGroupIndices`), which never moves a price.
 *
 * Prices are a PREVIEW (`@/lib/option-pricing-preview`, mirroring the engine's
 * money half); `addModifiersToCartAction` stores the picks and the cart
 * re-prices them server-side. Item-level sold-out and the required-group
 * cascade arrive resolved as `unavailable`/`unavailableReason`.
 */
export function ModifierCustomizer({
  item,
  locale,
  unavailable,
  unavailableReason,
  unavailableGroupTitle,
  closeOnAdd = false,
}: {
  item: MenuItem
  locale: string
  unavailable: boolean
  unavailableReason: ItemUnavailableReason | null
  unavailableGroupTitle?: string
  /** Dismiss the US-018 modal after a successful add (see {@link AddToCartForm}). */
  closeOnAdd?: boolean
}) {
  const t = useTranslations("customize")
  const groups = React.useMemo(
    () => item.modifierGroups ?? [],
    [item.modifierGroups]
  )
  const displayOrder = React.useMemo(
    () => displayGroupIndices(groups),
    [groups]
  )
  const config = React.useMemo(() => pricingConfigOf(item), [item])

  const [selection, setSelection] = React.useState<OptionPick[][]>(() =>
    initialSelection(groups, config)
  )
  const [quantity, setQuantity] = React.useState(MIN_QUANTITY)
  const [showMissing, setShowMissing] = React.useState(false)
  const fieldsets = React.useRef<(HTMLFieldSetElement | null)[]>([])

  function updateGroup(
    index: number,
    next: (group: ModifierGroup, picks: OptionPick[]) => OptionPick[]
  ) {
    setSelection((prev) => {
      const group = groups[index]
      if (!group) return prev
      const copy = [...prev]
      copy[index] = next(group, prev[index] ?? [])
      return copy
    })
  }

  const tap = (index: number, optionId: string) =>
    updateGroup(index, (group, picks) => {
      const result = tapOption(group, picks, optionId)
      return result.maxReached ? picks : result.picks
    })

  const decrement = (index: number, optionId: string) =>
    updateGroup(index, (_group, picks) => decrementOption(picks, optionId))

  const preview = React.useMemo(
    () => previewOptionSelection(item.basePrice, groups, selection, config),
    [config, groups, item.basePrice, selection]
  )
  const previewTotal = preview.unitPrice.amount * quantity
  const missingIndex = firstUnsatisfiedGroupIndex(groups, selection)

  const ruleCopy = (rule: SelectionRule) => selectionRuleCopy(rule, t)

  const unavailableMessage =
    unavailableReason === "sold_out"
      ? t("soldOut")
      : unavailableReason === "required_group_unavailable"
        ? t("requiredGroupUnavailable", { group: unavailableGroupTitle ?? "" })
        : null

  function guardIncomplete(event: React.MouseEvent<HTMLButtonElement>) {
    if (missingIndex === undefined) return
    event.preventDefault()
    setShowMissing(true)
    const fieldset = fieldsets.current[missingIndex]
    fieldset?.scrollIntoView({ behavior: "smooth", block: "center" })
    fieldset?.focus({ preventScroll: true })
  }

  return (
    <AddToCartForm
      action={addModifiersToCartAction}
      itemName={item.name}
      closeOnAdd={closeOnAdd}
      className="flex flex-col gap-6"
    >
      <input type="hidden" name="locale" value={locale} />
      <input type="hidden" name="itemId" value={item.id} />
      <input type="hidden" name="quantity" value={quantity} />
      <input
        type="hidden"
        name="optionSelection"
        value={JSON.stringify(selection)}
      />

      {unavailable && unavailableMessage ? (
        <p
          role="status"
          className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive"
        >
          {unavailableMessage}
        </p>
      ) : null}

      {displayOrder.map((index) => {
        const group = groups[index]
        if (!group) return null
        const picks = selection[index] ?? []
        const rows = preview.rowsByGroup[index] ?? []
        const missing = showMissing && !isGroupSatisfied(group, picks)
        return (
          <OptionGroupFieldset
            key={index}
            ref={(element) => {
              fieldsets.current[index] = element
            }}
            group={group}
            groupIndex={index}
            picks={picks}
            rows={rows}
            config={config}
            missing={missing}
            ruleCopy={ruleCopy(group.selection)}
            basePrice={item.basePrice}
            sizeId={preview.sizeId}
            locale={locale}
            onTap={(optionId) => tap(index, optionId)}
            onDecrement={(optionId) => decrement(index, optionId)}
          />
        )
      })}

      <SpecialInstructionsField
        label={t("specialInstructionsLabel")}
        placeholder={t("specialInstructionsPlaceholder")}
      />

      <div className="sticky bottom-0 z-10 flex flex-col gap-2 rounded-lg border bg-background p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div
            role="group"
            aria-label={t("quantityLabel")}
            className="flex shrink-0 items-center gap-1"
          >
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              disabled={quantity <= MIN_QUANTITY}
              aria-label={t("decreaseQuantity")}
              onClick={() => setQuantity((q) => Math.max(MIN_QUANTITY, q - 1))}
            >
              −
            </Button>
            <span
              aria-live="polite"
              className="w-6 text-center text-sm font-medium"
            >
              {quantity}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              disabled={quantity >= MAX_QUANTITY}
              aria-label={t("increaseQuantity")}
              onClick={() => setQuantity((q) => Math.min(MAX_QUANTITY, q + 1))}
            >
              +
            </Button>
          </div>

          <AddToCartSubmitButton
            disabled={unavailable}
            title={unavailable ? (unavailableMessage ?? undefined) : undefined}
            aria-describedby={
              unavailable && unavailableMessage
                ? "modifier-unavailable-note"
                : undefined
            }
            onClick={guardIncomplete}
            className="flex-1"
          >
            {unavailable
              ? t("soldOutButton")
              : t("addWithPrice", {
                  price: formatMoney(
                    {
                      amount: previewTotal,
                      currency: item.basePrice.currency,
                    },
                    locale
                  ),
                })}
          </AddToCartSubmitButton>
        </div>
        {showMissing && missingIndex !== undefined ? (
          <p role="alert" className="text-center text-xs text-destructive">
            {t("completeRequired", {
              group: groups[missingIndex]?.title ?? "",
            })}
          </p>
        ) : (
          <p className="text-center text-xs text-muted-foreground">
            {t("estimatedNote")}
          </p>
        )}
        {unavailable && unavailableMessage ? (
          <p
            id="modifier-unavailable-note"
            className="text-center text-xs text-muted-foreground"
          >
            {unavailableMessage}
          </p>
        ) : null}
      </div>
    </AddToCartForm>
  )
}

/** A group's "Choose up to 2" line, from the `customize` catalog. */
export function selectionRuleCopy(
  rule: SelectionRule,
  t: ReturnType<typeof useTranslations<"customize">>
): string {
  if (rule.kind === "single") {
    return rule.required
      ? t("ruleChooseExact", { n: 1 })
      : t("ruleChooseUpTo", { max: 1 })
  }
  const min = rule.min ?? (rule.required ? 1 : 0)
  const max = rule.max != null && rule.max > 0 ? rule.max : undefined
  if (min > 0 && max != null) {
    return min === max
      ? t("ruleChooseExact", { n: min })
      : t("ruleChooseRange", { min, max })
  }
  if (min > 0) return t("ruleChooseAtLeast", { min })
  if (max != null) return t("ruleChooseUpTo", { max })
  return t("ruleChooseAny")
}

/** One modifier group: header (rule, free counter, notes) + its option tiles. */
export function OptionGroupFieldset({
  ref,
  group,
  groupIndex,
  picks,
  rows,
  config,
  missing,
  ruleCopy,
  basePrice,
  sizeId,
  locale,
  onTap,
  onDecrement,
  hidePrices = false,
}: {
  ref: React.Ref<HTMLFieldSetElement>
  group: ModifierGroup
  groupIndex: number
  picks: OptionPick[]
  rows: PreviewRow[]
  config: OptionPricingConfig | undefined
  missing: boolean
  ruleCopy: string
  basePrice: MenuItem["basePrice"]
  sizeId?: string
  locale: string
  onTap: (optionId: string) => void
  onDecrement: (optionId: string) => void
  /** Options priced by something else (a freebie's flat charge covers its size) show no price. */
  hidePrices?: boolean
}) {
  const t = useTranslations("customize")
  const tFree = useTranslations("freeOption")
  const required = isSelectionRequired(group.selection)
  const ruleId = `group-rule-${groupIndex}`
  const errorId = `group-error-${groupIndex}`
  const size = isSizeGroup(group)
  const single = group.selection.kind === "single"
  const indicator = resolveSelectionIndicator(group.selection)
  const legacyFree =
    config?.freeOption && isLegacyFreeOptionGroup(group, config)
      ? config.freeOption
      : undefined
  const allowed = extrasAllowList(group, config)
  const atMax = isGroupAtMax(group, picks)

  // Tile state from the engine. A legacy-governed group's free units come from
  // the item-level resolver instead, so its tiles are computed without the
  // group-level allowance.
  const tiles = new Map(
    optionTiles(
      legacyFree ? { ...group, freeOption: undefined } : group,
      picks
    ).map((tile) => [tile.option.id, tile])
  )
  const legacyDefaults = legacyFree
    ? new Set(legacyFreeOptionDefaultIds(group, legacyFree))
    : undefined

  // "n of m free": what the allowance actually zeroed among the picks — never
  // more than the configured count.
  const freeConfig = legacyFree ?? group.freeOption
  const freeCapacity = Math.max(freeConfig?.freeCount ?? 0, 0)
  const freeUsed = Math.min(
    rows
      .filter(
        (row) =>
          row.free &&
          (!legacyFree || optionPriceDelta(row.option, sizeId).amount > 0)
      )
      .reduce((sum, row) => sum + row.quantity, 0),
    freeCapacity
  )

  function renderTile(option: ModifierOption) {
    const tile = tiles.get(option.id)
    if (!tile) return null
    const disallowed = allowed != null && !allowed.has(option.id)
    const disabled = !option.available || tile.isBlocked || disallowed
    const { price, free } = priceLabel(tile, option)

    const note = !option.available
      ? t("unavailable")
      : disallowed
        ? tFree("notInCombination")
        : tile.isIncluded
          ? t("includedWithAll")
          : legacyDefaults?.has(option.id)
            ? tFree("includedByDefault")
            : undefined

    return (
      <li key={option.id}>
        <OptionTile
          label={option.name}
          note={note}
          description={option.description}
          price={price}
          badge={free ? t("free") : undefined}
          selected={tile.isSelected}
          disabled={disabled}
          indicator={indicator}
          inputType={single ? "radio" : "checkbox"}
          inputName={single ? `group-${groupIndex}` : undefined}
          onToggle={() => onTap(option.id)}
          stepper={
            tile.type === "multiSelect"
              ? {
                  // The unit a select-all implied counts as one.
                  quantity: tile.quantity + (tile.isIncluded ? 1 : 0),
                  onIncrement: () => onTap(option.id),
                  onDecrement: () => onDecrement(option.id),
                  canIncrement: !atMax && !disallowed && option.available,
                  incrementLabel: t("addOne", { name: option.name }),
                  decrementLabel: t("removeOne", { name: option.name }),
                }
              : undefined
          }
        />
      </li>
    )
  }

  /** The trailing price copy for one tile, and whether it reads "Free". */
  function priceLabel(
    tile: OptionTileState,
    option: ModifierOption
  ): { price?: string; free: boolean } {
    if (hidePrices) return { free: false }
    // Sizes read as their real price, not a surcharge.
    if (size) {
      return {
        price: formatMoney(absoluteSizePrice(basePrice, option), locale),
        free: false,
      }
    }
    // An option a select-all implied costs nothing; its note says why. One
    // taken back out of it would re-join at no charge. Units added on top of
    // an implied multiSelect unit are priced below.
    if ((tile.isIncluded && tile.quantity === 0) || tile.nextPickIncluded) {
      return { free: false }
    }
    const delta = optionPriceDelta(option, sizeId)

    if (tile.quantity > 0) {
      const amount = optionAmount(rows, option)
      const coveredByAllowance = rows.some(
        (row) => row.option.id === option.id && row.free
      )
      if (amount === 0 && coveredByAllowance && delta.amount !== 0) {
        return { free: true }
      }
      return {
        price: formatMoneyDelta(
          { amount, currency: delta.currency },
          locale,
          t("free")
        ),
        free: tile.freeUnits > 0 && amount !== 0,
      }
    }

    const nextFree = legacyFree
      ? legacyNextUnitFree(group, picks, legacyFree, option.id)
      : tile.nextUnitFree
    if (nextFree && delta.amount !== 0) return { free: true }
    return { price: formatMoneyDelta(delta, locale, t("free")), free: false }
  }

  const orderOptions = (options: ModifierOption[]) =>
    size ? sortSizeOptions(options, locale) : options

  return (
    <fieldset
      ref={ref}
      tabIndex={-1}
      aria-describedby={missing ? `${ruleId} ${errorId}` : ruleId}
      aria-invalid={missing || undefined}
      className={cn(
        "flex flex-col gap-3 rounded-lg border p-4 outline-none",
        missing && "border-destructive"
      )}
    >
      <legend className="flex flex-wrap items-center gap-2 px-1 text-sm font-semibold">
        {group.title}
        <span
          className={cn(
            "ms-auto rounded-full px-2 py-0.5 text-xs font-medium",
            required
              ? "bg-primary/10 text-primary"
              : "bg-muted text-muted-foreground"
          )}
        >
          {required ? t("required") : t("optional")}
        </span>
      </legend>

      {group.description ? (
        <p className="px-1 text-xs text-muted-foreground">
          {group.description}
        </p>
      ) : null}
      <p id={ruleId} className="px-1 text-xs text-muted-foreground">
        {ruleCopy}
      </p>
      {freeCapacity > 0 ? (
        <p aria-live="polite" className="px-1 text-xs text-muted-foreground">
          {legacyFree
            ? tFree("freeCountUsed", { used: freeUsed, capacity: freeCapacity })
            : t("freeUsed", { used: freeUsed, capacity: freeCapacity })}
        </p>
      ) : null}
      {allowed ? (
        <p className="px-1 text-xs text-muted-foreground">
          {tFree("extrasLimitNote")}
        </p>
      ) : null}
      {missing ? (
        <p id={errorId} className="px-1 text-xs font-medium text-destructive">
          {t("requiredMissing")}
        </p>
      ) : null}

      {group.options ? (
        <OptionTileList indicator={indicator}>
          {orderOptions(group.options).map(renderTile)}
        </OptionTileList>
      ) : null}

      {group.subgroups?.map((subgroup) => (
        <div key={subgroup.title} className="flex flex-col gap-2">
          <p className="px-1 text-xs font-medium">{subgroup.title}</p>
          <OptionTileList indicator={indicator}>
            {orderOptions(subgroup.options).map(renderTile)}
          </OptionTileList>
        </div>
      ))}
    </fieldset>
  )
}
